






/**
 * This module extends the Leaflet library in order to display a TomTom map.
 *
 * For a full reference of the Leaflet map API check the official
 * <a href="http://leafletjs.com/reference-1.3.0.html" target="_blank">Leaflet's documentation</a>.
 *
 * We are using leaflet's version 1.3.0.
 *
 * If you want to use vector source in the map the basePath must be specified.
 * Keep this in mind when you switch from raster to vector source, for example when opening our examples in CodePen,
 * otherwise the map will not render properly.
 *
 * Example of how to create a basic map instance:
 *
 * ```html
 * <style type="text/css">
 *   html, body {
 *      width: 100%;
 *      height: 100%;
 *      margin: 0;
 *      padding: 0;
 *  }
 *  #map {
 *      width: 100%;
 *      height: 100%;
 *  }
 * </style>
 *
 * <div id="map"></div>
 *
 * <script>
 *   var mapInstance = new L.Map("map", {key: "<Your API key>"});
 * </script>
 * ```
 *
 * @class Map
 * @extends L.Map (Leaflet's)
 * @module Map
 * @namespace L
 * @constructor
 * @param {HTMLElement|String} id The div element or its id in which the map will be initialized.
 * @param {Object} [options] It accepts all the options that
 *     <a href="http://leafletjs.com/reference-1.3.0.html#map-options">Leaflet's Map</a>
 *     class allows.
 *     The additional options, which are TomTom specific, are described below.
 * @param {String} [options.key] API key that can be obtained from
 *     <a href="https://developer.tomtom.com">TomTom's developer portal</a>.
 *     When the key is passed on the map's initialization, it is set globally for all the services. It can be
 *     overwritten with the `tomtom.key()` method globally or per service  with `tomtom.<service_name>.key()` functions.
 *     It is also possible to pass a specific key while working with instances of the services.
 * @param {"main"|"night"} [options.style="main"] The base map style to be used.
 * @param {"basic"|"hybrid"|"labels"} [options.layer="basic"] The base map's layer to be used.
 * @param {"Unified"|"IN"} [options.geopolView] Define the geopolitical view for the new map instance.
 * @param {Boolean|Object} [options.traffic] Allows to easily add the
 *     {{#crossLink "L.TomTomTrafficLayer"}}{{/crossLink}} and
 *     {{#crossLink "L.TomTomTrafficIncidentsLayer"}}{{/crossLink}} layers to the map. A value of this parameter can
 *     be set to an options object as described in the traffic layer documentation.
 *     If traffic layers are added with this option, a mechanism for periodic refresh of the traffic data is provided
 *     (using a proper traffic model id handling). If you pass source parameter set to vector, {{#crossLink "L.TomTomVectorTrafficIncidentsLayer"}}{{/crossLink}}
 *     will be added as a source for incident tiles instead of raster one.
 * @param {Boolean} [options.traffic.noRefresh] Disables periodic updates of traffic layers.
 * @param {Number} [options.traffic.refresh=30000] The traffic incidents information changes and needs to be updated
 *     periodically. By default, it is refreshed every 30 seconds. This parameter's value represents the time
 *     in milliseconds between each update interval. The value must be 0 or greater.
 * @param {Boolean|Object} [options.trafficFlow] Allows to add {{#crossLink "L.TomTomTrafficFlowLayer"}}{{/crossLink}}
 *     to the map. A value of this parameter can be set to the options object as described in the traffic flow layer
 *     documentation. If you specify source set to vector as one of the parameters it will add
 *    {{#crossLink "L.TomTomVectorTrafficFlowLayer"}}{{/crossLink}} instead.
 * @param {Boolean|Object} [options.vectorTrafficFlow] Allows to add {{#crossLink "L.TomTomVectorTrafficFlowLayer"}}
 * {{/crossLink}} to the map. A value of this parameter can be set to the options object as described in the traffic
 * flow layer documentation.
 * @param {Boolean} [options.noLabelsOnTop=false] If a traffic or traffic flow layer is overlayed on the map, then by
 *     default, the labels layer is added on top of these layers. It prevents labels from being covered by the traffic
 *     tubes or traffic flow. With this option, it is possible to disable this behaviour.
 * @param {String|String[]} [options.source="raster"] Sets the initial source and determines which tiles types will be
 *     displayed (raster, vector). If an array is given first item is used as a default source and next items are
 *     initialized but not displayed. If MapboxGL JS dependency is not met vector layer will not initialize.
 * @param {String} [options.glyphsUrl] Glyphs are fonts in PBF required to display labels on vector maps. The Url must
 * contain the {fontstack} and {range} tokens. The SDK distributable already contains the glyphs required by the default
 * vector styles - you just need to provide a path to them on your server.
 * Example of a URL format for this property:
 * ```javascript
 * "http://yourdomain/glyphs/{fontstack}/{range}.pbf"
 * ```
 * @param {String} [options.basePath = '/sdk'] Base path for sdk. It will be used in relative paths - it is especially
 * important for maps with vector sources. E.g if you set this option to `"/myPath"`, default urls for styles will
 * be set to `/myPath/styles/basic_main.json`.
 * This property is used only if you do not provide custom mapping for given url mapping.
 * @param {Object} [options.styleUrlMapping] Overrides the default URLs for vector style files.
 * The default value is as follows:
 * ```javascript
 * {
 *   main: {
 *      basic: '/sdk/styles/basic_main.json',
 *      hybrid: '/sdk/styles/hybrid_main.json',
 *      labels: '/sdk/styles/labels_main.json'
 *   },
 *   night: {
 *      basic: '/sdk/styles/basic_night.json',
 *      hybrid: '/sdk/styles/hybrid_night.json',
 *      labels: '/sdk/styles/labels_night.json'
 *   }
 * }
 * ```
 * @param {String} [options.spriteUrl] Set the base URL for the sprite image and metadata. The extensions .png, .json
 * and scale factor will be appended at the end of URL automatically. The sprite URL is required to display icons
 * like road shields on vector maps.
 * Example of a URL format for this property:
 * ```javascript
 * "http://yourdomain/sprite/sprite"
 * ```
 * @param {Boolean} [options.zoomControl=true] Show a zoom control at the top left corner of the map.
 * @param {Boolean} [options.panControl=false] Enable the pan controls at the top left corner of the map.
 * @param {Boolean} [options.zoomsliderControl=false] Shows a vertical slider to control the zoom level.
 * It is recommended to disable the default zoom control option (`zoomControl=false`) when you use this feature.
 * @param {Boolean} [options.worldCopyJump=true]
 * @param {String} [options.language] Language to be used for map labels. Should be one of supported
 *      IETF language tags described
 *      <a href="https://developer.tomtom.com/maps-api/maps-api-documentation-raster/raster-tile#listOfSupportedLanguages">here</a>.
 *      When data in specified language is not available for a specific label,
 *      <a href="https://developer.tomtom.com/maps-api/maps-api-documentation-raster/raster-tile#defaultLanguageAlgorithm">default language</a>
 *      is used.
 */


/* This method is implemented in staticMapImageFactory.js but documented here due to a
   yuidoc's bug in the @for tag */
/**
 * Returns an URL string to obtain an static image version of the current map's viewport.
 *
 * This method uses the {{#crossLink "StaticMapImage"}}{{/crossLink}} class to generate an URL to get static image
 * based on the configuration of the current map's instance.
 *
 * @method getStaticMapUrl
 * @return {String} An URL string to the image
 */
/* jshint nomen:false */
define([
    'leaflet',
    './svgIcon',
    './imagePath',
    'mapboxProvider',
    'security',
    'services/copyrights/copyrightCaption',
    './copyright',
    './layers/trafficWithIncidents',
    './layers/trafficFlowLayer',
    './layers/vector/vectorTrafficFlowLayer',
    './layers/vector/vectorTrafficWithIncidents',
    './layers/labelsLayer',
    './layers/vector/vectorLabelsLayer',
    './layers/tileLayer',
    './layers/vector/vectorLayer',
    '../config/unit/unitService',
    '../config/unit/globalUnitService',
    '../config/language/localeService',
    '../config/language/globalLocaleService',
    '../config/geopolView/geopolViewService',
    '../config/geopolView/globalGeopolViewService',
    'services/routing/routingContext',
    'services/utils',
    './mapUtils',
    './layerOptionsUpdater'
],
function(
    L,
    svgIcon,
    imagePath,
    mapboxProvider,
    security,
    copyrightCaption,
    copyright,
    trafficWithIncidents,
    trafficFlow,
    vectorTrafficFlow,
    vectorTrafficIncidents,
    labelsLayer,
    VectorLabelsLayer,
    tileLayer,
    VectorLayer,
    UnitService,
    globalUnitService,
    LocaleService,
    globalLocaleService,
    GeopolViewService,
    globalGeopolViewService,
    RoutingContext,
    utils,
    mapUtils
) {
    'use strict';
    var EVENTS = {
        /**
         * Fired when attribution was fully loaded. Loaded attribution is passed as event data.
         * @event ATTRIBUTION_LOAD_END
         * @type String
         */
        ATTRIBUTION_LOAD_END: 'tomtom.attributionloadend'
    };

    function roundZoom(map, source) {
        if (source === 'raster') {
            map.setZoom(Math.floor(map.getZoom()));
        }
    }

    L.Map.Events = EVENTS;

    L.Map.mergeOptions({
        worldCopyJump: true
    });

    delete L.Map.prototype.options.minZoom;
    delete L.Map.prototype.options.maxZoom;

    /**
     * Method allows to change a source data for currently shown map. It should be one of sources provided
     * at creation as a property 'options.source'
     *
     * @method setMapSource
     * @param {"raster"|"vector"} source
     */
    L.Map.prototype.setMapSource = function(source) {
        var baseLayers, map, requestedLayer, baseLayer;
        map = this;
        baseLayers = map._ttBaseLayers;
        requestedLayer = baseLayers[source];
        if (!requestedLayer) {
            return;
        }

        for (baseLayer in baseLayers) {
            if (baseLayers.hasOwnProperty(baseLayer)) {
                this.removeLayer(baseLayers[baseLayer]);
            }
        }

        roundZoom(map, source);

        map.addLayer(requestedLayer);
    };

    /**
     * @method getBaseLayers
     * @return {Object} Returns base layers initialized on map
     */
    L.Map.prototype.getBaseLayers = function() {
        return this._ttBaseLayers;
    };

    /**
     * Returns localeService which is bound to this map instance. Please note
     * that if you change value in globalLocaleService this one will be updated
     * too, but not other way around.
     * @method getLocaleService
     * @return {LocaleService}
     */
    L.Map.prototype.getLocaleService = function() {
        return this._localeService;
    };

    /**
     * Returns unitService which is bound to this map instance. Please note
     * that if you change the unit of the global UnitService instance this one will be updated
     * too, but not way around.
     * @method getUnitService
     * @return {UnitService}
     */
    L.Map.prototype.getUnitService = function() {
        return this._unitService;
    };

    /**
     * Returns the GeopolViewService instance which is bound to the map instance.
     * Please note that if you change the geopolitical view of the
     * global L.GeopolViewService instance this one will be updated too
     * but not way around.
     * @method getGeopolViewService
     * @return {GeopolViewService}
     */
    L.Map.prototype.getGeopolViewService = function() {
        return this._geopolViewService;
    };

    L.Control.Attribution.prototype.options = {
        position: 'bottomleft',
        prefix: ''
    };

    L.Icon.Default = svgIcon;
    L.Icon.Default.imagePath = imagePath.get();
    L.Marker.mergeOptions({ icon: new L.Icon.Default() });

    function removeInvalidSources(source) {
        var allowedSources = /^(raster|vector)$/;
        var sourceLenght = source.length;
        var validSource = [];
        var currentSource, i;

        for (i = 0; i < sourceLenght; i++) {
            currentSource = String(source[i]).toLowerCase();
            if (allowedSources.test(source[i])) {
                validSource.push(currentSource);
            }
        }

        return validSource;
    }

    function getSource(map) {
        var source = map.options.source || ['raster'];
        var defaultSource = ['raster'];

        if (utils.isString(source)) {
            source = [source];
        }

        source = removeInvalidSources(source);

        return source.length > 0 ? source : defaultSource;
    }

    function isOlderThanIE11() {
        return window.navigator.userAgent.indexOf('MSIE ') > -1;
    }

    function copyBasePathIfNeeded(map, property) {
        if (!map.options[property].basePath) {
            map.options[property].basePath = map.options.basePath;
        }
    }

    function initTrafficLayer(map) {
        if (map.options.traffic) {
            if (map.options.traffic.source && map.options.traffic.source === 'vector') {
                copyBasePathIfNeeded(map, 'traffic');
                map.addLayer(vectorTrafficIncidents(map.options.traffic));
            } else {
                map.addLayer(trafficWithIncidents(map.options.traffic));
            }
        }
    }

    function initTrafficFlowLayer(map) {
        var vectorLayerAdded = false;
        if (map.options.trafficFlow) {
            if (map.options.trafficFlow.source && map.options.trafficFlow.source === 'vector') {
                copyBasePathIfNeeded(map, 'trafficFlow');
                map.addLayer(vectorTrafficFlow(map.options.trafficFlow));
                vectorLayerAdded = true;
            } else {
                map.addLayer(trafficFlow(map.options.trafficFlow));
            }
        }
        if (map.options.vectorTrafficFlow && !vectorLayerAdded) {
            copyBasePathIfNeeded(map, 'vectorTrafficFlow');
            map.addLayer(vectorTrafficFlow(map.options.vectorTrafficFlow));
        }
    }

    function initLabelsLayers(map) {
        var source = getSource(map);
        var layerInstance;
        var vectorLabelsInstance;
        map._ttLabelsLayers = {};

        if (source.indexOf('raster') !== -1 && !map.options.noLabelsOnTop && !isOlderThanIE11()) {
            //This will not work on IE < 11 due to not supported CSS: pointer-events: none;
            layerInstance = labelsLayer(map.options);
            layerInstance.registerEventHandlers(map);
            L.stamp(layerInstance);
            map._ttLabelsLayers.raster = layerInstance;
        }

        if (source.indexOf('vector') !== -1 && !map.options.noLabelsOnTop && mapboxProvider.isMapboxSet()) {
            vectorLabelsInstance = new VectorLabelsLayer(map.options);
            vectorLabelsInstance.registerEventHandlers(map);
            L.stamp(vectorLabelsInstance);
            map._ttLabelsLayers.vector = vectorLabelsInstance;
        }
    }

    function initZoomFix(map) {
        var mac = navigator.platform === 'MacIntel';
        if (!mac) {
            return;
        }
        map._handlers.filter(function(handler) {
            return '_performZoom' in handler;
        }).forEach(function(handler) {
            var original = handler._performZoom;
            handler._performZoom = function() {
                handler._delta *= 13.25;
                original.apply(handler);
            };
        });
    }

    function createWebGLErrorDiv(map) {
        var div = L.DomUtil.create('div', 'tomtom-webgl-error');
        div.appendChild(document.createTextNode('Unable to initialize vector layer - browser doesn\'t support WebGL!'));
        if (map._controlContainer) {
            map._controlContainer.appendChild(div);
        }
    }

    function isWebGlSupported() {
        var canvas = document.createElement('canvas');
        var webGlContext = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        if (webGlContext && webGlContext instanceof WebGLRenderingContext) {
            return true;
        }

        return false;
    }

    function resolveMapboxSupport(map) {
        var defaultFallback = 'error';
        var allowedFallbacks = /^(error|raster)$/;
        var source = getSource(map);
        var fallback = map.options.vector ? String(map.options.vector.fallback).toLowerCase() : '';
        fallback = allowedFallbacks.test(fallback) ? fallback : defaultFallback;

        if (source.indexOf('vector') === -1 || (mapboxProvider.isMapboxSupported() && isWebGlSupported())) {
            return;
        }

        switch (fallback) {
        case 'raster':
            map.options.source = ['raster'];
            break;
        case 'error':
            if (isWebGlSupported()) {
                throw new Error('MapBox failed to load. Make sure you\'re using vector library.');
            }

            createWebGLErrorDiv(map);
            throw new Error('Unable to initialize vector layer - browser doesn\'t support WebGL!');
        default:
            throw new Error('Something went wrong with vectorFallback!');
        }
    }

    function getMapOptionsWithGeopolView(map, mapLayer) {
        var options;
        if (!map.options.geopolView) {
            return map.options;
        }
        options = utils.clone(map.options);
        options.geopolView = map._geopolViewService.getCurrentGeopolView().getValueFor('maps', mapLayer);
        return options;
    }

    function recreateLayer(layer) {
        var map = this,
            currentLayer = map._ttBaseLayers ? map._ttBaseLayers[layer] : undefined,
            newLayer;
        /**
         * @ignore
         * Even if we do not make layer active right now, we need to keep up-to-date reference of
         * vector/raster layer. There is a public method 'getBaseLayers()' which is used in map-style.html
         * example, which uses those references to change between vector and raster.
         * So, if we change language while raster is active, vector/raster should be updated too,
         * in case when user wants to add layer using those references
        */
        if (currentLayer) {
            newLayer = layer === 'vector' ?
                new VectorLayer(getMapOptionsWithGeopolView(map, 'vector')) :
                new L.TomTomLayer(getMapOptionsWithGeopolView(map, 'raster'));
            L.stamp(newLayer);
            map._ttBaseLayers[layer] = newLayer;
            if (mapUtils.findLayersByName(layer, map).length > 0) {
                map.addLayer(newLayer);
                map.removeLayer(currentLayer);
            }
        }
    }

    function mapLocaleChangedHandler(locale) {
        this.options.language = locale.getValueFor('maps');
        recreateLayer.call(this, 'vector');
        recreateLayer.call(this, 'raster');
    }

    function mapGeopolViewChangedHandler(geopolView) {
        this.options.geopolView = geopolView.getCode();
        recreateLayer.call(this, 'vector');
        recreateLayer.call(this, 'raster');
    }

    function initBaseLayer(map) {
        var source = getSource(map);
        var layers = [];
        var i;
        map._ttBaseLayers = {};

        if (source.indexOf('raster') !== -1) {
            map._ttBaseLayers.raster = tileLayer(getMapOptionsWithGeopolView(map, 'raster'));
            L.stamp(map._ttBaseLayers.raster);
        }

        if (mapboxProvider.tryToSetMapbox() && source.indexOf('vector') !== -1) {
            map._ttBaseLayers.vector = new VectorLayer(getMapOptionsWithGeopolView(map, 'vector'));
            L.stamp(map._ttBaseLayers.vector);
        }

        for (i = 0; i < map.options.layers.length; i++) {
            if (map.options.layers[i].wmsParams) {
                layers.push(L.tileLayer.wms(map.options.layers[i]._url, map.options.layers[i].wmsParams));
            }
        }

        if (!layers.length) {
            switch (source[0]) {
            case 'vector':
                if (map._ttBaseLayers.vector === undefined) {
                    throw new Error('Can not add vector layer. Mapbox-gl-js dependency not met.');
                }
                layers = [map._ttBaseLayers.vector];
                break;
            case 'raster':
                layers = [map._ttBaseLayers.raster];
                break;
            default:
                throw new Error('Something went wrong with sources!');
            }
        }

        map.options.layers = layers;
    }

    /**
     * @ignore
     * This function ensures that the custom zoom configuration given by the user has the correct format.
     * It convert string numbers to numbers. That's all.
     * @param {L.Map} map
     */
    function initZoomConfig(map) {
        if (isFinite(map.options.zoomDelta)) {
            map.options.zoomDelta = parseFloat(map.options.zoomDelta);
        }
        if (isFinite(map.options.zoomSnap)) {
            map.options.zoomSnap = parseFloat(map.options.zoomSnap);
        }
        if (isFinite(map.options.wheelPxPerZoomLevel)) {
            map.options.wheelPxPerZoomLevel = parseInt(map.options.wheelPxPerZoomLevel, 10);
        }
    }

    function initRoutingContext(map) {
        map._routingContext = new RoutingContext();
    }

    function cleanUp(map) {
        map._localeService.cleanEvents();
        map._geopolViewService.cleanEvents();
    }

    function installUnitServiceSupport(map) {
        map._unitService = new UnitService(globalUnitService);
    }

    function installLocaleServiceSupport(map) {
        map._localeService = new LocaleService(globalLocaleService);
        map._localeService.on(L.LocaleService.Events.LOCALE_CHANGED, mapLocaleChangedHandler.bind(map));
        map._localeService.setCurrentLocale(map.options.language);
        map.options.language = map._localeService.getCurrentLocale().getLanguageCode();
    }

    function installGeopolViewServiceSupport(map) {
        map._geopolViewService = new GeopolViewService(globalGeopolViewService);
        map._geopolViewService.on(L.GeopolViewService.Events.GEOPOL_VIEW_CHANGED,
            mapGeopolViewChangedHandler.bind(map));
        if (map.options.geopolView) {
            map._geopolViewService.setCurrentGeopolView(map.options.geopolView);
        }
    }

    function initHook() {
        var map = this,
            attributionMarkup = '',
            originalAddAttribution;
        L.Icon.Default.imagePath = imagePath.get(this);
        installUnitServiceSupport(map);
        installLocaleServiceSupport(map);
        installGeopolViewServiceSupport(map);
        try {
            resolveMapboxSupport(map);
            security.setKey(map.options.key);
            initZoomConfig(map);
            initBaseLayer(map);
            initLabelsLayers(map);
            initTrafficFlowLayer(map);
            initTrafficLayer(map);
            initRoutingContext(map);
            initZoomFix(map);

            if (map.getZoom() === undefined) {
                map.setView([0, 0], 2);
            }
            if (!map.options.maxBounds) {
                map.setMaxBounds(L.latLngBounds([[-90, -18000], [90, 18000]]));
            }
            copyrightCaption().go(function(attribution) {
                if (map.attributionControl) {
                    originalAddAttribution = map.attributionControl.addAttribution;
                    map.attributionControl.addAttribution = function() {
                        originalAddAttribution.apply(this, arguments);
                        copyright.updateEventHandler(map);
                    };

                    attributionMarkup += '<a class="tomtomAttribution">';
                    attributionMarkup += attribution;
                    attributionMarkup += '</a>';
                    map.attributionControl.addAttribution(attributionMarkup);
                    map.fire(L.Map.Events.ATTRIBUTION_LOAD_END, {data: attributionMarkup});
                }
            });
        } catch (err) {
            console.error(err);
            cleanUp(map);
            throw err;
        }

    }

    L.Map.addInitHook(initHook);

    return L;
});



// WEBPACK FOOTER //
// ./display/map.js